import React, { useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
const FocusTrap = require('focus-trap-react');

import styles from './Modal.module.scss';

let noActiveModals = 0;

const useBodyPortal = () => {
    let modalRootRef = useRef(null);

    useEffect(() => {
        modalRootRef.current = document.createElement('div');

        const ref = modalRootRef.current;
        const modalRoot = document.body;

        modalRoot.appendChild(ref);

        return () => {
            ref.remove();
        };
    }, []);

    return modalRootRef.current;
};

const Modal = ({ children, open, handleClose, showCloseButton, closeLabel = '' }) => {
    const { t } = useTranslation('common');
    const wrapperRef = useRef(null);
    const modalContainer = useBodyPortal();
    let handleKeypress;

    // restore scroll pos
    useEffect(() => {
        if (!open) {
            return;
        }

        noActiveModals++;

        const origScrollPos = window.pageYOffset || document.documentElement.scrollTop;

        document.addEventListener('keydown', handleKeypress, false);
        document.body.style.overflow = 'hidden';

        return () => {
            // handle nested/multiple modals correctly
            noActiveModals--;
            if (noActiveModals === 0) {
                document.removeEventListener('keydown', handleKeypress, false);
                document.body.style.overflow = 'auto';

                window.scrollTo(0, origScrollPos);
            }
        };
    }, [open, handleKeypress]);

    // set focus on mount
    useEffect(() => {
        if (!open || !wrapperRef.current) {
            return;
        }

        wrapperRef.current.focus();
    }, [open]);

    handleKeypress = useCallback(
        (e) => {
            e.stopPropagation();

            if (e.keyCode === 27 && typeof handleClose === 'function') {
                if (noActiveModals < 2 || wrapperRef.current.contains(document.activeElement)) {
                    // handle nested/multiple modals correctly
                    handleClose(e);
                }
            }
        },
        [handleClose]
    );

    const handleOutsideClick = useCallback(
        (e) => {
            e.persist();
            e.stopPropagation();

            if (typeof handleClose === 'function' && wrapperRef.current && wrapperRef.current === e.target) {
                handleClose(e);
            }
        },
        [handleClose]
    );

    if (!open || !modalContainer) {
        return null;
    }

    return ReactDOM.createPortal(
        <FocusTrap>
            <div className={styles['Modal']}>
                {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
                <div
                    role="dialog"
                    aria-labelledby="modal-title"
                    aria-modal="true"
                    tabIndex="-1"
                    ref={wrapperRef}
                    className={styles['Modal__Inner']}
                    onKeyPress={handleKeypress}
                    onClick={handleOutsideClick}>
                    {showCloseButton && (
                        <button className={styles['Modal__CloseButton']} onClick={handleClose}>
                            <span className="sr-only">{closeLabel || t('modal.close')}</span>
                        </button>
                    )}
                    {children}
                </div>
            </div>
        </FocusTrap>,
        modalContainer
    );
};

Modal.propTypes = {
    open: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    handleClose: PropTypes.func,
    showCloseButton: PropTypes.bool,
    closeLabel: PropTypes.string,
};

Modal.defaultProps = {
    handleClose: () => {},
    showCloseButton: true,
};

export default Modal;
